import * as THREE from 'three';
import { QUALITY, type QualityLevel } from '../config';

interface Particle {
  pos: THREE.Vector3;
  vel: THREE.Vector3;
  life: number;
  maxLife: number;
  size: number;
  color: THREE.Color;
  active: boolean;
}

interface FloatText {
  el: HTMLDivElement;
  life: number;
  vy: number;
}

export class VFXSystem {
  private scene: THREE.Scene;
  private uiRoot: HTMLElement;
  private particles: Particle[] = [];
  private points!: THREE.Points;
  private geometry!: THREE.BufferGeometry;
  private positions!: Float32Array;
  private colors!: Float32Array;
  private sizes!: Float32Array;
  private maxParticles: number;
  private floatTexts: FloatText[] = [];
  private flashMesh: THREE.Mesh | null = null;
  private quality: QualityLevel = 'high';

  constructor(scene: THREE.Scene, uiRoot: HTMLElement) {
    this.scene = scene;
    this.uiRoot = uiRoot;
    this.maxParticles = QUALITY.high.particles;
    this.initPoints();
  }

  setQuality(q: QualityLevel): void {
    this.quality = q;
    this.maxParticles = QUALITY[q].particles;
  }

  private initPoints(): void {
    this.geometry = new THREE.BufferGeometry();
    this.positions = new Float32Array(this.maxParticles * 3);
    this.colors = new Float32Array(this.maxParticles * 3);
    this.sizes = new Float32Array(this.maxParticles);
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(this.colors, 3));
    this.geometry.setAttribute('size', new THREE.BufferAttribute(this.sizes, 1));

    const mat = new THREE.PointsMaterial({
      size: 0.18,
      vertexColors: true,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    });

    this.points = new THREE.Points(this.geometry, mat);
    this.points.frustumCulled = false;
    this.scene.add(this.points);

    for (let i = 0; i < this.maxParticles; i++) {
      this.particles.push({
        pos: new THREE.Vector3(),
        vel: new THREE.Vector3(),
        life: 0,
        maxLife: 1,
        size: 0.1,
        color: new THREE.Color(),
        active: false,
      });
    }
  }

  burst(x: number, y: number, color: number, count: number, speed = 3): void {
    const n = Math.min(count, this.maxParticles);
    let spawned = 0;
    for (const p of this.particles) {
      if (spawned >= n) break;
      if (p.active) continue;
      p.active = true;
      p.pos.set(x, y, 0.2);
      const a = Math.random() * Math.PI * 2;
      const s = speed * (0.4 + Math.random() * 0.8);
      p.vel.set(Math.cos(a) * s, Math.sin(a) * s + 1.2, (Math.random() - 0.5) * 0.8);
      p.life = 0.4 + Math.random() * 0.5;
      p.maxLife = p.life;
      p.size = 0.08 + Math.random() * 0.16;
      p.color.setHex(color);
      // slight variation
      p.color.offsetHSL((Math.random() - 0.5) * 0.08, 0, (Math.random() - 0.5) * 0.1);
      spawned++;
    }
  }

  ringBurst(x: number, y: number, color: number, radius: number): void {
    const count = Math.floor(12 + radius * 10);
    let spawned = 0;
    for (const p of this.particles) {
      if (spawned >= count) break;
      if (p.active) continue;
      p.active = true;
      const a = (spawned / count) * Math.PI * 2;
      p.pos.set(x + Math.cos(a) * radius * 0.3, y + Math.sin(a) * radius * 0.3, 0.15);
      p.vel.set(Math.cos(a) * 2.5, Math.sin(a) * 2.5, 0);
      p.life = 0.35;
      p.maxLife = 0.35;
      p.size = 0.12;
      p.color.setHex(color);
      spawned++;
    }
  }

  flash(x: number, y: number, color: number, scale: number): void {
    if (this.quality === 'low') return;
    if (this.flashMesh) {
      this.scene.remove(this.flashMesh);
      (this.flashMesh.material as THREE.Material).dispose();
      this.flashMesh.geometry.dispose();
    }
    const geo = new THREE.CircleGeometry(scale, 24);
    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.65,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.flashMesh = new THREE.Mesh(geo, mat);
    this.flashMesh.position.set(x, y, 0.3);
    this.scene.add(this.flashMesh);
  }

  floatScore(x: number, y: number, text: string, isCombo: boolean): void {
    const el = document.createElement('div');
    el.className = 'float-score' + (isCombo ? ' combo' : '');
    el.textContent = text;
    el.style.left = '50%';
    el.style.top = '40%';
    // Approximate world-to-screen is handled loosely — parent centers mid playfield
    el.style.transform = `translate(${x * 28}px, ${-y * 22}px)`;
    this.uiRoot.appendChild(el);
    this.floatTexts.push({ el, life: 1.0, vy: -40 });
  }

  update(dt: number): void {
    let i = 0;
    for (const p of this.particles) {
      if (!p.active) {
        this.positions[i * 3] = 0;
        this.positions[i * 3 + 1] = -999;
        this.positions[i * 3 + 2] = 0;
        this.sizes[i] = 0;
        i++;
        continue;
      }
      p.life -= dt;
      if (p.life <= 0) {
        p.active = false;
        this.sizes[i] = 0;
        i++;
        continue;
      }
      p.vel.y -= 4 * dt;
      p.pos.addScaledVector(p.vel, dt);
      p.vel.multiplyScalar(0.98);
      const lifeR = p.life / p.maxLife;
      this.positions[i * 3] = p.pos.x;
      this.positions[i * 3 + 1] = p.pos.y;
      this.positions[i * 3 + 2] = p.pos.z;
      this.colors[i * 3] = p.color.r;
      this.colors[i * 3 + 1] = p.color.g;
      this.colors[i * 3 + 2] = p.color.b;
      this.sizes[i] = p.size * lifeR;
      i++;
    }
    (this.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    (this.geometry.attributes.color as THREE.BufferAttribute).needsUpdate = true;
    (this.geometry.attributes.size as THREE.BufferAttribute).needsUpdate = true;

    // Custom shader size — PointsMaterial uses uniform size; scale via opacity on material
    const mat = this.points.material as THREE.PointsMaterial;
    mat.opacity = 0.85;

    if (this.flashMesh) {
      const m = this.flashMesh.material as THREE.MeshBasicMaterial;
      m.opacity -= dt * 3.5;
      this.flashMesh.scale.multiplyScalar(1 + dt * 4);
      if (m.opacity <= 0) {
        this.scene.remove(this.flashMesh);
        m.dispose();
        this.flashMesh.geometry.dispose();
        this.flashMesh = null;
      }
    }

    for (let fi = this.floatTexts.length - 1; fi >= 0; fi--) {
      const ft = this.floatTexts[fi]!;
      ft.life -= dt;
      ft.vy -= 20 * dt;
      ft.el.style.opacity = String(Math.max(0, ft.life));
      if (ft.life <= 0) {
        ft.el.remove();
        this.floatTexts.splice(fi, 1);
      }
    }
  }

  dispose(): void {
    this.scene.remove(this.points);
    this.geometry.dispose();
    (this.points.material as THREE.Material).dispose();
    for (const ft of this.floatTexts) ft.el.remove();
    this.floatTexts = [];
  }
}
